/**
 * Presets de período e a aritmética de janela, compartilhados entre o
 * Dashboard, a tela de Menções e o Relatório CENA. Eram locais do
 * `GlobalDashboardPage`; extraídos quando a segunda tela precisou dos
 * mesmos presets — copiar significaria "últimos 30 dias" passar a
 * querer dizer coisas diferentes em duas telas no dia em que alguém
 * ajustasse uma só.
 *
 * **A janela do relatório tem HORA desde 2026-08-27.** Antes ela era um
 * dia (`date` no banco, `"2026-03-31"` no wire) e a tela de Menções já
 * filtrava por hora — as duas telas não conseguiam concordar sobre o
 * mesmo conjunto. Agora as duas falam de instante. As regras de borda
 * são as MESMAS de
 * `core/report_control/criteria.py::normalize_window_instant`
 * (fim-de-dia fecha em `:59`, microsegundo zero); duplicá-las aqui não
 * é ideal, mas a alternativa — perguntar ao servidor a cada tecla —
 * seria pior, e o servidor normaliza de novo de todo jeito. Se um dia
 * divergirem, o servidor vence e a tela mostra um conjunto diferente do
 * relatório: é o sintoma a procurar.
 *
 * **NENHUMA CONVERSÃO DE FUSO ACONTECE AQUI (decisão da dona,
 * 2026-08-29).** `published_at` é a HORA DE PAREDE da clipadora: o ETL
 * a grava sem fuso, de propósito (`elt/domain/parsing.py`), porque é o
 * horário que a matéria carrega na planilha. Logo os dígitos SÃO a
 * resposta, nos dois sentidos — o que a pessoa digita vai como está, e
 * o que o servidor devolve é lido como está.
 *
 * Até esta data o arquivo convertia navegador→UTC na saída
 * (`toISOString()`) e lia como local na volta. Num navegador em
 * Brasília isso custava 3 horas em cada ponta: "de 01/03" excluía os
 * posts só-data do dia 01/03, e "até 31/03 23:59" trazia posts de
 * 01/04 pra dentro da foto de março. Não aparecia internamente (tela e
 * relatório deslocavam JUNTOS), só contra a planilha da clipadora — que
 * é justamente o número que o analista confere.
 *
 * Cuidado ao copiar daqui: carimbo de VERDADE (o `created_at` de um
 * upload, por exemplo) é instante de verdade e continua sendo
 * convertido — ver `parseServerTime` em `upload-metrics.ts`. A regra de
 * "os dígitos são o relógio" vale pra janela e pra publicação, que são
 * o mesmo dado da clipadora.
 */

import { formatDateTime, formatShortDate } from "@/shared/utils/format";

export type RangePreset = "7d" | "30d" | "90d" | "year" | "12m" | "all" | "custom";

/**
 * O dia de um `Date`, no fuso de quem está olhando.
 *
 * Getters locais e não `toISOString().slice(0, 10)`: aquele converte
 * para UTC antes de cortar, então em Brasília (UTC-3) qualquer preset
 * calculado depois das 21h devolvia o dia SEGUINTE. O "últimos 30 dias"
 * da noite pegava um dia a mais nas duas pontas, sem sintoma nenhum.
 */
export function isoDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function rangeDates(preset: RangePreset): { from?: string; to?: string } {
  if (preset === "all") return {};
  const to = new Date();
  const from = new Date(to);
  if (preset === "year") from.setMonth(0, 1);
  else if (preset === "12m") from.setFullYear(from.getFullYear() - 1);
  else from.setDate(from.getDate() - (preset === "7d" ? 6 : preset === "90d" ? 89 : 29));
  return { from: isoDate(from), to: isoDate(to) };
}

/** Primeiro e último dia do mês corrente — o recorte natural de um
 * relatório mensal, que os presets de "últimos N dias" não dão. */
export function currentMonthRange(): { from: string; to: string } {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return { from: isoDate(first), to: isoDate(last) };
}

export function previousMonthRange(): { from: string; to: string } {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const last = new Date(today.getFullYear(), today.getMonth(), 0);
  return { from: isoDate(first), to: isoDate(last) };
}

/**
 * Um `Date` a partir do que a API mandou, seja dia ou instante.
 *
 * O `T12:00:00` não é decoração: `new Date("2026-08-01")` é lido como
 * meia-noite UTC, que em Brasília (UTC-3) cai no dia 31 de julho — a
 * janela aparecia um dia menor nas duas pontas. O meio-dia local
 * sobrevive a qualquer fuso entre -11 e +12.
 *
 * Quando a string JÁ traz hora, o `T12:00:00` não entra — grudá-lo numa
 * string que já tem `T` produziria `Invalid Date`, que viraria "Invalid
 * Date" no nome sugerido do relatório, ou seja, dado gravado errado e
 * não só exibição torta.
 *
 * **O fuso da string é DESCARTADO, não aplicado** (2026-08-29). A janela
 * volta do servidor rotulada UTC (`normalize_window_instant` carimba o
 * fuso), mas o que ela guarda é relógio de parede da clipadora — ver o
 * cabeçalho do módulo. Aplicar esse rótulo mostraria "28 de fev. 21:00"
 * numa janela que a pessoa abriu em 01 de março.
 */
export function localDate(iso: string): Date {
  if (!iso.includes("T")) return new Date(`${iso}T12:00:00`);
  return new Date(dropZoneSuffix(iso));
}

/** Tira o `Z` ou o `±HH:MM` do fim de um instante, deixando só o
 *  relógio de parede — que é o que o JS parseia como hora local. */
function dropZoneSuffix(iso: string): string {
  return iso.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "");
}

/**
 * O valor de um `<input type="datetime-local">` a partir de um instante
 * da API.
 *
 * O input REJEITA valor com offset (`2026-08-01T00:00:00Z` zera o
 * campo, sem erro): ele só aceita `YYYY-MM-DDTHH:mm` no fuso de quem
 * olha. Sem esta conversão, abrir um rascunho existente apagaria a
 * janela do formulário e o botão de recalcular ficaria desabilitado por
 * campo vazio.
 */
export function toLocalInput(iso: string): string {
  const moment = localDate(iso);
  const hours = `${moment.getHours()}`.padStart(2, "0");
  const minutes = `${moment.getMinutes()}`.padStart(2, "0");
  return `${isoDate(moment)}T${hours}:${minutes}`;
}

/**
 * O instante que vai pro servidor, a partir do relógio de parede que a
 * pessoa digitou.
 *
 * `bound` é o que faz "até 23:59" significar o minuto INTEIRO: sem
 * isso, uma menção publicada às 23:59:30 ficaria de fora de uma janela
 * que a tela diz ir até 23:59. É a mesma regra que o servidor aplica —
 * e é ela que faz o total anotado na tela de Menções bater com o total
 * do relatório gerado a partir dele.
 *
 * Devolve `undefined` para entrada vazia, que é o que as duas telas
 * usam pra dizer "sem essa ponta".
 *
 * **Sai SEM fuso** (2026-08-29): o relógio que a pessoa digitou é o
 * relógio que o servidor compara, porque `published_at` é hora de
 * parede da clipadora. Era `toISOString()` até esta data, e as 3 horas
 * de Brasília entravam como recorte fantasma nas duas pontas — ver o
 * cabeçalho do módulo. O back lê naive como está
 * (`core/database/timestamps.py::as_naive_utc`).
 */
export function localInstant(
  day: string,
  time: string,
  bound: "start" | "end"
): string | undefined {
  if (!day) return undefined;
  const wall = time || (bound === "start" ? "00:00" : "23:59");
  const moment = new Date(`${day}T${wall}`);
  if (Number.isNaN(moment.getTime())) return undefined;
  moment.setMilliseconds(0);
  // Segundo explícito (a pessoa digitou `:30`) é respeitado; o input
  // sem `step` não os oferece, então na prática isto fecha o minuto.
  if (bound === "end" && moment.getSeconds() === 0) moment.setSeconds(59);
  return wallClockIso(moment);
}

/** O instante como `YYYY-MM-DDTHH:mm:ss`, sem fuso: os dígitos do
 *  relógio local, que é o que o servidor compara. */
function wallClockIso(moment: Date): string {
  const hours = `${moment.getHours()}`.padStart(2, "0");
  const minutes = `${moment.getMinutes()}`.padStart(2, "0");
  const seconds = `${moment.getSeconds()}`.padStart(2, "0");
  return `${isoDate(moment)}T${hours}:${minutes}:${seconds}`;
}

/**
 * O instante a partir do valor cru de um `<input type="datetime-local">`
 * (`YYYY-MM-DDTHH:mm`), que é como a tela de relatório guarda a janela —
 * lá as duas metades não são campos separados, como são em Menções.
 */
export function instantFromLocalInput(
  value: string,
  bound: "start" | "end"
): string | undefined {
  const { day, time } = splitLocalInput(value);
  return localInstant(day, time, bound);
}

/** Quebra um valor de `datetime-local` (ou um instante da API) nas duas
 *  metades que as telas guardam separadas. */
export function splitLocalInput(value: string): { day: string; time: string } {
  if (!value) return { day: "", time: "" };
  const local = value.includes("Z") || /[+-]\d{2}:\d{2}$/.test(value) ? toLocalInput(value) : value;
  const [day, time = ""] = local.split("T");
  return { day, time: time.slice(0, 5) };
}


export function formatReportWindow(windowStart: string, windowEnd: string): string {
  return `${formatShortDate(localDate(windowStart))} – ${formatShortDate(localDate(windowEnd))}`;
}

/** A janela é o dia inteiro, dos dois lados? Aí a hora é ruído. */
function isWholeDay(start: Date, end: Date): boolean {
  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    start.getSeconds() === 0 &&
    end.getHours() === 23 &&
    end.getMinutes() === 59 &&
    end.getSeconds() === 59
  );
}

/**
 * O rótulo da janela COM hora, quando a hora quer dizer alguma coisa.
 *
 * Um relatório gerado pelos presets vai de 00:00:00 a 23:59:59 — a
 * mesma coisa que "01 de ago. – 31 de ago." já dizia, e escrever a hora
 * ali só empurraria o resto da linha. A hora aparece quando ela
 * RECORTA: é o caso que vem da tela de Menções.
 */
export function formatReportWindowDetailed(windowStart: string, windowEnd: string): string {
  const start = localDate(windowStart);
  const end = localDate(windowEnd);
  if (isWholeDay(start, end)) return formatReportWindow(windowStart, windowEnd);
  return `${formatDateTime(start)} – ${formatDateTime(end)}`;
}

/**
 * A janela como a FOTO a congelou, e não como as colunas a guardam.
 *
 * A distinção existe por causa das linhas anteriores à migração
 * `f1c4e07a92d6`: nelas a janela era um DIA, e a migração a converteu
 * pra instante escolhendo `00:00:00`/`23:59:59` em UTC. Convertidas
 * pro fuso de quem olha, essas pontas viram "28 de fev. 21:00" — uma
 * hora que ninguém escolheu, num dia que não é o do relatório.
 *
 * Por isso o rótulo pergunta ao `schema_version`: a versão 1 é a que a
 * migração inventou, e dela só o DIA é afirmação verdadeira. Da versão
 * 2 em diante o instante foi escolhido por alguém e é exibido inteiro.
 */
export function formatWindowFromCriteria(
  criteria: { schema_version: number; window_start?: string; window_end?: string } | null | undefined,
  windowStart: string,
  windowEnd: string
): string {
  if (criteria && criteria.schema_version < 2) {
    const start = criteria.window_start?.slice(0, 10) ?? windowStart.slice(0, 10);
    const end = criteria.window_end?.slice(0, 10) ?? windowEnd.slice(0, 10);
    return formatReportWindow(start, end);
  }
  return formatReportWindowDetailed(windowStart, windowEnd);
}
